import React, { useState } from "react";
import FormInput from "@/components/ui/form-input";
import { Button } from "../ui/button/Button";
import TextArea from "@/components/ui/form-input/TextArea";
import { useCallToAction } from "@/hooks/useCallToAction";
import type { CallToActionCompletionRequest } from "@/services/api/agentic/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import { faXmark } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/ui/dialog/Dialog";
import { AgenticAPI } from "@/services/api";

function normalizeAcceptedFileTypes(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string");
  }
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function PriorityBar({ priority }: { priority: string }) {
  let color = "";
  switch ((priority || "").toLowerCase()) {
    case "high":
      color = "bg-red-400";
      break;
    case "medium":
      color = "bg-yellow-400";
      break;
    case "low":
      color = "bg-green-400";
      break;
    default:
      color = "bg-slate-400";
  }
  return <div className={`w-2 rounded-full ${color}`}></div>;
}

export function CallToAction({ action }: { action: any }) {
  const meta = action.metadata || {};
  const priority = action.priority ?? meta.priority;
  const { completeAction, isLoading, error, clearError } = useCallToAction();
  const [isSuccess, setIsSuccess] = useState(false);
  const [clickedButton, setClickedButton] = useState<string | null>(null);
  const [showDismissDialog, setShowDismissDialog] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  
  // Move all useState hooks to component level to comply with Rules of Hooks
  const [textInputValue, setTextInputValue] = useState("");
  const [numberInputValue, setNumberInputValue] = useState("");
  const [selectedOptions, setSelectedOptions] = useState<string[] | string>(
    meta.allow_multiple ? [] : ""
  );
  const [chatMessage, setChatMessage] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileDescription, setFileDescription] = useState("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Dismiss handlers
  const handleDismissClick = () => {
    setShowDismissDialog(true);
  };

  const handleDismissConfirm = async () => {
    setIsDismissing(true);
    try {
      const actionId = action.id || action._id;
      await AgenticAPI.cancelCallToAction(actionId);
      setIsDismissed(true);
      setShowDismissDialog(false);
    } catch (err) {
      console.error("Failed to dismiss call-to-action:", err);
    } finally {
      setIsDismissing(false);
    }
  };

  const handleDismissCancel = () => {
    setShowDismissDialog(false);
  };

  // Helper component to wrap each remedy type with dismiss button
  const ActionWrapper = ({ children }: { children: React.ReactNode }) => (
    <>
      <div className="flex gap-4 items-stretch relative">
        {priority && <PriorityBar priority={priority} />}
        <div className="flex-1">{children}</div>
        <button
          onClick={handleDismissClick}
          className="absolute top-0 right-0 p-2 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors"
          title="Dismiss action"
        >
          <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
        </button>
      </div>

      {/* Dismiss confirmation dialog */}
      <Dialog open={showDismissDialog} onClose={handleDismissCancel} size="sm">
        <DialogTitle>Dismiss Action</DialogTitle>
        <DialogBody>
          <p className="text-gray-700 dark:text-gray-300">
            Are you sure you want to dismiss this action? This will mark it as
            cancelled.
          </p>
        </DialogBody>
        <DialogActions>
          <Button onClick={handleDismissCancel} disabled={isDismissing}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleDismissConfirm}
            disabled={isDismissing}
          >
            {isDismissing ? "Dismissing..." : "Dismiss"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  const handleSubmit = async (request: CallToActionCompletionRequest) => {
    const actionId = action.id || action._id;

    // Check if this is a sample/test call-to-action
    if (actionId?.startsWith("sample_")) {
      console.log("Sample call-to-action completed (mock):", {
        id: actionId,
        request,
        message:
          "This is a sample call-to-action - no actual API call was made",
      });

      // You could show a success message or simulate the completion
      // For now, we'll just log it and not make an actual API call
      return;
    }

    try {
      const response = await completeAction(actionId, request);
      console.log("Call-to-action completed:", response);

      // Clear any previous errors on success
      clearError();

      // Set success state
      setIsSuccess(true);

      // Reset clicked button state
      setClickedButton(null);

      // You can add success handling here, such as:
      // - Refreshing the parent component
      // - Navigating to the next step
    } catch (err) {
      console.error("Failed to complete call-to-action:", err);
      // Error is already handled by the hook
      setIsSuccess(false);
      setClickedButton(null);
    }
  };

  // If dismissed, show a simple dismissed message
  if (isDismissed) {
    return (
      <div className="flex gap-4 items-stretch">
        {priority && <PriorityBar priority={priority} />}
        <div className="flex-1 text-slate-500 dark:text-slate-400 italic">
          Action dismissed
        </div>
      </div>
    );
  }

  // text_input
  if (action.remedy_type === "text_input") {
    const handleSubmitText = () => {
      if (!textInputValue.trim()) return;

      handleSubmit({
        remedy_type: "text_input",
        value: textInputValue.trim(),
      });
    };

    return (
      <ActionWrapper>
        <div className="mb-3 ">{action.message}</div>
        {isSuccess ? (
          <div className="mt-2 text-green-500 text-sm flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Submitted successfully!
          </div>
        ) : (
          <>
            <FormInput
              type="text"
              placeholder={meta.placeholder}
              minLength={meta.min_length}
              maxLength={meta.max_length}
              required={meta.required}
              value={textInputValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTextInputValue(e.target.value)
              }
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSubmitText();
                }
              }}
            />
            {error && <div className="mt-2 text-red-500 text-sm">{error}</div>}
            <div className="mt-4">
              <Button
                color="indigo"
                onClick={handleSubmitText}
                disabled={isLoading || !textInputValue.trim()}
              >
                {isLoading ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </>
        )}
      </ActionWrapper>
    );
  }

  // number_input
  if (action.remedy_type === "number_input") {
    const handleSubmitNumber = () => {
      const numValue = parseFloat(numberInputValue);
      if (isNaN(numValue)) return;

      handleSubmit({
        remedy_type: "number_input",
        value: numValue,
      });
    };

    return (
      <ActionWrapper>
        <div className="mb-1 text-sm">{action.message}</div>
        {isSuccess ? (
          <div className="mt-2 text-green-500 text-sm flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Submitted successfully!
          </div>
        ) : (
          <>
            <FormInput
              type="number"
              placeholder={meta.placeholder}
              min={meta.min_value}
              max={meta.max_value}
              step={meta.step}
              required={meta.required}
              value={numberInputValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNumberInputValue(e.target.value)
              }
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSubmitNumber();
                }
              }}
            />
            {error && <div className="mt-2 text-red-500 text-sm">{error}</div>}
            <div className="mt-4">
              <Button
                color="indigo"
                onClick={handleSubmitNumber}
                disabled={isLoading || !numberInputValue || isNaN(parseFloat(numberInputValue))}
              >
                {isLoading ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </>
        )}
      </ActionWrapper>
    );
  }

  // button_action
  if (action.remedy_type === "button_action") {
    const handleButtonClick = (buttonAction: string) => {
      setClickedButton(buttonAction);
      handleSubmit({
        remedy_type: "button_action",
        button_action: buttonAction,
      });
    };

    return (
      <ActionWrapper>
        <div className="mb-1 text-sm">{action.message}</div>
        {isSuccess ? (
          <div className="mt-2 text-green-400 text-sm flex items-center gap-2">
            <FontAwesomeIcon icon={faCheck} />
            Action completed successfully!
          </div>
        ) : (
          <>
            {error && <div className="mt-2 text-red-500 text-sm">{error}</div>}
            <div className="flex gap-2 mt-4">
              {(meta.buttons || []).map((btn: any, i: number) => (
                <Button
                  key={i}
                  color={btn.color || "color"}
                  onClick={() => handleButtonClick(btn.action)}
                  disabled={isLoading && clickedButton === btn.action}
                >
                  {isLoading && clickedButton === btn.action
                    ? "Processing..."
                    : btn.label}
                </Button>
              ))}
            </div>
          </>
        )}
      </ActionWrapper>
    );
  }

  // document_upload
  if (action.remedy_type === "document_upload") {
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        setFileName(e.target.files[0].name);
      } else {
        setFileName(null);
      }
    };

    const handleClick = () => {
      fileInputRef.current?.click();
    };

    const handleSubmitDocument = () => {
      if (!fileInputRef.current?.files?.[0]) return;

      handleSubmit({
        remedy_type: "document_upload",
        file: fileInputRef.current.files[0],
        ...(fileDescription && { description: fileDescription }),
      });
    };

    return (
      <ActionWrapper>
        <div className="mb-1 text-sm">{action.message}</div>
        {isSuccess ? (
          <div className="mt-2 text-green-500 text-sm flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Document uploaded successfully!
          </div>
        ) : (
          <>
            <div className="mt-4 bg-slate-100 dark:bg-slate-800/70 p-2 rounded-2xl border border-slate-200/60 dark:border-transparent">
              <input
                type="file"
                className="hidden"
                ref={fileInputRef}
                accept={normalizeAcceptedFileTypes(
                  meta.accepted_file_types
                ).join(",")}
                onChange={handleFileChange}
              />
              <div className="flex items-center gap-4">
                <Button color="indigo" onClick={handleClick}>
                  Choose File
                </Button>
                <span className="font-mono font-normal text-slate-600 dark:text-slate-400 text-sm">
                  {fileName || "No file chosen"}
                </span>
              </div>
            </div>
            {fileName && (
              <div className="mt-2">
                <TextArea
                  placeholder="Optional description..."
                  value={fileDescription}
                  onChange={(e) => setFileDescription(e.target.value)}
                  className="text-sm"
                />
              </div>
            )}
            {fileName && (
              <div className="mt-2">
                <Button
                  color="indigo"
                  onClick={handleSubmitDocument}
                  disabled={isLoading}
                >
                  {isLoading ? "Uploading..." : "Upload"}
                </Button>
              </div>
            )}
            {error && <div className="mt-2 text-red-400 text-sm">{error}</div>}
          </>
        )}
      </ActionWrapper>
    );
  }

  // options
  if (action.remedy_type === "options") {
    const handleSubmitOptions = () => {
      const optionsToSubmit = meta.allow_multiple
        ? Array.isArray(selectedOptions)
          ? selectedOptions
          : []
        : typeof selectedOptions === "string"
          ? [selectedOptions]
          : [];

      if (optionsToSubmit.length === 0) return;

      handleSubmit({
        remedy_type: "options",
        selected_options: optionsToSubmit,
      });
    };

    return (
      <ActionWrapper>
        <div className="mb-1 text-sm">{action.message}</div>
        {isSuccess ? (
          <div className="mt-2 text-green-500 text-sm flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Options submitted successfully!
          </div>
        ) : (
          <>
            <div className="mt-2 space-y-2">
              {(meta.options || []).map((opt: string | { label: string; value: string }, i: number) => {
                const optValue = typeof opt === "object" ? opt.value : opt;
                const optLabel = typeof opt === "object" ? opt.label : opt;
                return (
                  <label
                    key={i}
                    className="flex items-center text-sm gap-2 cursor-pointer"
                  >
                    <input
                      type={meta.allow_multiple ? "checkbox" : "radio"}
                      name={action.id || action._id}
                      value={optValue}
                      checked={
                        meta.allow_multiple
                          ? Array.isArray(selectedOptions) && selectedOptions.includes(optValue)
                          : selectedOptions === optValue
                      }
                      onChange={(e) => {
                        if (meta.allow_multiple) {
                          setSelectedOptions((prev) => {
                            const arr = Array.isArray(prev) ? prev : [];
                            return e.target.checked
                              ? [...arr, optValue]
                              : arr.filter((v) => v !== optValue);
                          });
                        } else {
                          setSelectedOptions(optValue);
                        }
                      }}
                      className="sr-only peer"
                    />
                    {meta.allow_multiple ? (
                      <div className="w-4 h-4 shrink-0 bg-white border-2 rounded-sm border-slate-300 dark:border-slate-500 dark:bg-slate-700 peer-checked:bg-indigo-600 peer-checked:border-indigo-600 dark:peer-checked:bg-indigo-500 dark:peer-checked:border-indigo-500 flex justify-center items-center">
                        <svg
                          className="w-3 h-3 text-white hidden peer-checked:block"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                    ) : (
                      <div className="relative w-3 h-3 shrink-0 rounded-full bg-white ring-2 ring-slate-300 ring-offset-2 ring-offset-white peer-checked:bg-indigo-600 peer-checked:ring-indigo-600 dark:bg-slate-700 dark:ring-slate-500 dark:ring-offset-slate-800 dark:peer-checked:bg-indigo-500 dark:peer-checked:ring-indigo-500"></div>
                    )}
                    <span className="ml-2 text-slate-700 dark:text-slate-300">{optLabel}</span>
                  </label>
                );
              })}
            </div>
            {error && <div className="mt-2 text-red-500 text-sm">{error}</div>}
            <div className="mt-4">
              <Button
                color="indigo"
                onClick={handleSubmitOptions}
                disabled={
                  isLoading ||
                  (meta.allow_multiple
                    ? Array.isArray(selectedOptions) && selectedOptions.length === 0
                    : !selectedOptions)
                }
              >
                {isLoading ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </>
        )}
      </ActionWrapper>
    );
  }

  // redirect_to_chat
  if (action.remedy_type === "redirect_to_chat") {
    const handleSubmitChat = () => {
      if (!chatMessage.trim()) return;

      handleSubmit({
        remedy_type: "redirect_to_chat",
        message: chatMessage.trim(),
        ...(meta.context && { context: meta.context }),
      });
    };

    return (
      <ActionWrapper>
        <div className="mb-1 text-sm">{action.message}</div>
        {isSuccess ? (
          <div className="mt-2 text-green-500 text-sm flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Message sent successfully!
          </div>
        ) : (
          <>
            {meta.context && <div className="">{meta.context}</div>}
            {Array.isArray(meta.suggested_questions) &&
              meta.suggested_questions.length > 0 && (
                <ul className="mt-1 ml-5 list-disc ">
                  {meta.suggested_questions.map((q: string, i: number) => (
                    <li key={i}>{q}</li>
                  ))}
                </ul>
              )}
            <TextArea
              className="mt-2 p-3 min-h-24 w-full rounded-xl bg-slate-800/70"
              placeholder="Provide more information..."
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
            />
            {error && <div className="mt-2 text-red-500 text-sm">{error}</div>}
            <div className="mt-4">
              <Button
                color="indigo"
                onClick={handleSubmitChat}
                disabled={isLoading || !chatMessage.trim()}
              >
                {isLoading ? "Sending..." : "Send Message"}
              </Button>
            </div>
          </>
        )}
      </ActionWrapper>
    );
  }

  // fallback
  return (
    <ActionWrapper>
      <div className="p-2 bg-slate-100 rounded text-xs">
        {action.message || action.remedy_type}
      </div>
    </ActionWrapper>
  );
}
